import useAuth from '@/hooks/useAuth';
import IEventType from '@/types/IEventType';
import { Avatar, Box, ButtonGroup, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { FiEdit, FiTrash } from 'react-icons/fi';
import { useParticipantItem } from './participant-item.controller';

interface IUserItemProps {
	isLoading: boolean;
	eventType: IEventType;
	resourceId: string;
	onEditAvailabilityClick: (id: string) => void;
}

export default function ParticipantItem(args: IUserItemProps) {
	const auth = useAuth();
	const { resourcesQuery, t, handleRemoveClick } = useParticipantItem({
		eventType: args.eventType,
		participantId: args.resourceId,
	});

	if (!resourcesQuery.data) {
		return null;
	}

	const participant = resourcesQuery.data.resources.find((r) => r.id === args.resourceId);

	if (!participant) {
		return (
			<Box p={4} borderColor="gray.200" borderWidth={1} borderRadius="md">
				<Text fontWeight="bold">⚠️ {t('Unknown availability')}</Text>
				<Text fontSize="sm">{t('Someone has added an availability that you do not have access to.')}</Text>
			</Box>
		);
	}

	return (
		<Flex align="center" gap={3} px={4} py={2} borderColor="gray.200" borderWidth={1} borderRadius="md">
			<Avatar size="sm" name={participant.name} />
			<Flex direction="column" fontSize="xs">
				<Text fontWeight="medium">{participant.name}</Text>
				<Tooltip hasArrow label={t('This availability belongs to {{email}}', { email: participant.owner.email })}>
					<Text color="gray.500">{participant.owner.email}</Text>
				</Tooltip>
			</Flex>
			<Box ml="auto">
				<ButtonGroup isAttached size="sm" variant="ghost">
					<Tooltip label={t('Edit availability')}>
						<IconButton
							display={participant.owner.id !== auth.getCurrentUser().userId ? 'none' : 'flex'}
							icon={<FiEdit />}
							aria-label="Edit"
							onClick={() => args.onEditAvailabilityClick(participant.id)}
						/>
					</Tooltip>
					<Tooltip label={t('Remove')}>
						<IconButton isLoading={args.isLoading} onClick={handleRemoveClick} icon={<FiTrash />} aria-label="Remove" />
					</Tooltip>
				</ButtonGroup>
			</Box>
		</Flex>
	);
}
